// src/pages/portal/PortalPerfilPage.tsx
import { useState } from 'react';
import {
  usePortalPerfil,
  useActualizarPortalPerfil,
  useCambiarPortalPassword,
} from '@/hooks/queries';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Skeleton,
  Avatar,
} from '@/components/ui';
import { User, Building2, Phone, Lock, Save } from 'lucide-react';

function PerfilSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-48" />
      <Skeleton className="h-64" />
      <Skeleton className="h-48" />
    </div>
  );
}

export default function PortalPerfilPage() {
  const { data: perfil, isLoading } = usePortalPerfil();
  const actualizarPerfil = useActualizarPortalPerfil();
  const cambiarPassword = useCambiarPortalPassword();

  const [editMode, setEditMode] = useState(false);
  const [perfilData, setPerfilData] = useState({
    nombre: '',
    apellido: '',
    telefono: '',
  });

  const [passwordData, setPasswordData] = useState({
    password_actual: '',
    password_nuevo: '',
    password_confirmar: '',
  });
  const [passwordError, setPasswordError] = useState('');

  // Inicializar datos cuando se carga el perfil
  if (perfil && !perfilData.nombre) {
    setPerfilData({
      nombre: perfil.nombre || '',
      apellido: perfil.apellido || '',
      telefono: perfil.telefono || '',
    });
  }

  const handleActualizarPerfil = () => {
    actualizarPerfil.mutate(
      {
        nombre: perfilData.nombre || undefined,
        apellido: perfilData.apellido || undefined,
        telefono: perfilData.telefono || undefined,
      },
      {
        onSuccess: () => setEditMode(false),
      }
    );
  };

  const handleCambiarPassword = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordData.password_nuevo !== passwordData.password_confirmar) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.password_nuevo.length < 8) {
      setPasswordError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    cambiarPassword.mutate(
      {
        password_actual: passwordData.password_actual,
        password_nuevo: passwordData.password_nuevo,
      },
      {
        onSuccess: () => {
          setPasswordData({
            password_actual: '',
            password_nuevo: '',
            password_confirmar: '',
          });
        },
      }
    );
  };

  if (isLoading) {
    return <PerfilSkeleton />;
  }

  if (!perfil) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">
          No se pudo cargar el perfil
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
        Mi Perfil
      </h1>

      {/* Info del usuario */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <Avatar
              fallback={`${perfil.nombre} ${perfil.apellido || ''}`}
              size="lg"
            />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {perfil.nombre} {perfil.apellido}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">{perfil.email}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Building2 className="w-4 h-4" />
              <span>{perfil.cliente_nombre || 'Sin cliente'}</span>
            </div>
            {perfil.telefono && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Phone className="w-4 h-4" />
                <span>{perfil.telefono}</span>
              </div>
            )}
          </div>

          {/* Empresas */}
          {perfil.empresas.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Empresas asociadas:
              </p>
              <div className="flex flex-wrap gap-2">
                {perfil.empresas.map((empresa) => (
                  <span
                    key={empresa.id}
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-sm text-gray-700 dark:text-gray-300"
                  >
                    {empresa.nombre}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editar datos */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5" />
            Datos Personales
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editMode ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="nombre">Nombre</Label>
                <Input
                  id="nombre"
                  value={perfilData.nombre}
                  onChange={(e) =>
                    setPerfilData({ ...perfilData, nombre: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="apellido">Apellido</Label>
                <Input
                  id="apellido"
                  value={perfilData.apellido}
                  onChange={(e) =>
                    setPerfilData({ ...perfilData, apellido: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  value={perfilData.telefono}
                  onChange={(e) =>
                    setPerfilData({ ...perfilData, telefono: e.target.value })
                  }
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleActualizarPerfil}
                  disabled={actualizarPerfil.isPending}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Guardar
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Nombre</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {perfil.nombre}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Apellido</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {perfil.apellido || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {perfil.email}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Teléfono</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {perfil.telefono || '-'}
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={() => setEditMode(true)}>
                Editar datos
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cambiar contraseña */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Cambiar Contraseña
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCambiarPassword} className="space-y-4">
            <div>
              <Label htmlFor="password_actual">Contraseña actual</Label>
              <Input
                id="password_actual"
                type="password"
                value={passwordData.password_actual}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, password_actual: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="password_nuevo">Nueva contraseña</Label>
              <Input
                id="password_nuevo"
                type="password"
                value={passwordData.password_nuevo}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, password_nuevo: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="password_confirmar">Confirmar nueva contraseña</Label>
              <Input
                id="password_confirmar"
                type="password"
                value={passwordData.password_confirmar}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, password_confirmar: e.target.value })
                }
                required
              />
            </div>
            {passwordError && (
              <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
            )}
            <Button type="submit" disabled={cambiarPassword.isPending}>
              Cambiar contraseña
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
